<template>
	<precoa-modal
		class="sm:w-160! sm:max-w-none!"
		:open="open"
		@close="close"
		:modal-title="t('reviewSlide.manualAdd')"
	>
		<template #default>
			<div class="flex flex-col gap-2 pt-2">
				<div class="my-2 text-center text-sm text-slate-500"></div>
				<FormKit
					type="form"
					@submit="onSubmit"
					:actions="false"
					v-model="manualAddItem"
				>
					<div class="grid grid-cols-2 gap-2">
						<FormKit
							type="dropdown"
							:label="t('merchandise.category')"
							name="category"
							outer-class="max-w-full w-full"
							listbox-class="max-h-60 overflow-y-auto"
							:deselect="false"
							:options="categoryOptions"
							validation="required"
							@input="onCategoryInput"
						/>
						<FormKit
							id="subcategory-dropdown"
							type="dropdown"
							:label="t('merchandise.subcategory')"
							name="subcategory"
							outer-class="max-w-full w-full"
							listbox-class="max-h-60 overflow-y-auto"
							:deselect="false"
							:options="subcategoryOptions"
						>
							<template #option="{ option, classes }">
								<div
									:class="[
										classes.option,
										{ 'cursor-not-allowed opacity-50': option.attrs?.disabled },
									]"
								>
									{{ option.label }}
								</div>
							</template>
						</FormKit>
					</div>
					<FormKit
						type="text"
						:label="t('reviewSlide.title')"
						name="title"
						outer-class="max-w-full w-full"
						validation="required"
					/>
					<div class="flex flex-wrap gap-2">
						<TransitionGroup name="list">
							<div
								v-for="field in subCategoryFields"
								:key="field.prop"
								class="h-20.5 flex-1"
							>
								<FormKit
									:type="field.type"
									:label="field.label"
									:name="field.prop"
									:validation="field.required ? 'required' : ''"
									outer-class="max-w-full w-full"
								/>
							</div>
						</TransitionGroup>
					</div>
					<div class="mb-4 border-t border-slate-100"></div>
					<div class="flex gap-2">
						<FormKit
							type="currency"
							currency="USD"
							:label="t('reviewSlide.amount')"
							name="price"
							outer-class="max-w-full flex-1"
							aria-required="true"
							validation="required"
						/>
						<div
							v-if="manualAddItem.subcategory != PbSubcategory.Discount"
							class="flex items-center gap-1 pt-2 pl-2"
						>
							<FormKit
								type="checkbox"
								name="guaranteed"
								:model-value="isGuaranteedItem"
								outer-class="max-w-fit! grow-0! mb-0! flex items-center"
								inner-class="place-items-center"
								track-color-on="#8AE4A4"
								readonly
								:disabled="true"
							/>
							<div class="text-sm font-bold text-slate-950">
								{{ t('reviewSlide.guaranteedItem') }}
							</div>
						</div>
					</div>
					<button
						type="submit"
						class="bg-accent-400! text-accent-700! hover:bg-accent-700! disabled:bg-secondary-300! mt-4 w-full rounded-lg p-3 text-center font-bold hover:text-white!"
					>
						{{ t('reviewSlide.add') }}
					</button>
				</FormKit>
			</div>
		</template>
	</precoa-modal>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import { DefaultCustomItemOptions } from '@/components/documents/CustomItems/DefaultCustomItemOptions'
import { NorthCarolinaCustomItems } from '@/components/documents/Foxit/FormMapping/NGL/NorthCarolina/NGLNCCustomItems'
import { TexasCustomItems } from '@/components/documents/Foxit/FormMapping/NGL/Texas/NGLTXCustomItems'
import { UtahCustomItems } from '@/components/documents/Foxit/FormMapping/NGL/Utah/NGLUTCustomItems'
import { WestVirginiaCustomItems } from '@/components/documents/Foxit/FormMapping/NGL/WestVirginia/NGLWVCustomItems'
import { WisconsinCustomItems } from '@/components/documents/Foxit/FormMapping/NGL/Wisconsin/NGLWICustomItems'
import type { SubcategoryOption } from '@/data/stateSubcategoryOptions'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import { useSessionStore } from '@/stores/session'
import type { CustomCartItem, SubcategoryProperties } from '@/types'
import { FormKit, reset as formkitReset } from '@formkit/vue'
import { computed, type PropType, ref, watch } from 'vue'

const customItemConfig: SubcategoryProperties = {
	DEFAULT: DefaultCustomItemOptions,
	NC: NorthCarolinaCustomItems,
	TX: TexasCustomItems,
	UT: UtahCustomItems,
	WI: WisconsinCustomItems,
	WV: WestVirginiaCustomItems,
}

/**
 * The form values are a Custom Cart Item minus id and isSoftDeleted.
 * The category and subcategory are optional.
 */
type FormProps = Omit<CustomCartItem, 'id' | 'isSoftDeleted' | 'category'> &
	Partial<Pick<CustomCartItem, 'category'>>

const { t } = usePresentationLocale()
const sessionStore = useSessionStore()
const emit = defineEmits<{
	(e: 'submit', item: FormProps & Pick<CustomCartItem, 'category'>): void
	(e: 'close'): void
}>()

const props = defineProps({
	open: {
		type: Boolean,
		default: false,
	},
	plan: {
		type: Object as PropType<Plan>,
		default: null,
	},
	packageIncluded: {
		type: Boolean,
		default: false,
	},
})

const initialValue = {
	title: '',
	shortDescription: '',
	toggle: true,
	price: 0,
	guaranteed: false,
	category: undefined,
	subcategory: undefined,
}

const manualAddItem = ref<FormProps>({
	...initialValue,
})

// All categories except OAP
const categoryOptions = computed(() => {
	const stateCode = sessionStore.activeFh?.billingAddress?.stateCode
	const config =
		stateCode && customItemConfig[stateCode]
			? customItemConfig[stateCode]
			: customItemConfig['DEFAULT']

	return Object.keys(config ?? {}).map((category) => ({
		label: t(`productCategories.${category}`),
		value: category as PbProductType,
	}))
})

const subCategoryFields = computed(() => {
	if (!manualAddItem.value.subcategory || !manualAddItem.value.category) {
		return []
	}

	const stateCode = sessionStore.activeFh?.billingAddress?.stateCode
	const config =
		stateCode && customItemConfig[stateCode]
			? customItemConfig[stateCode][manualAddItem.value.category]
			: customItemConfig['DEFAULT'][manualAddItem.value.category]

	if (!config) {
		return []
	}

	return config[manualAddItem.value.subcategory as PbSubcategory]
})

// Subcategory options based on the category and state
const subcategoryOptions = computed<SubcategoryOption[]>(() => {
	if (!manualAddItem.value.category) {
		return []
	}

	const stateCode = sessionStore.activeFh?.billingAddress?.stateCode
	const category = manualAddItem.value.category
	const config =
		stateCode && customItemConfig[stateCode]
			? customItemConfig[stateCode][category]
			: customItemConfig['DEFAULT'][category]

	const customItemOptions: SubcategoryOption[] =
		Object.keys(config ?? {}).map((subcategory) => {
			const isDisabled =
				category === PbProductType.Transportation &&
				subcategory === PbSubcategory.Other2 &&
				props.packageIncluded

			const label = isDisabled
				? `${t(`productSubcategories.${subcategory}`)} ${t('reviewSlide.customItemDropdownDisabledMessage')}`
				: t(`productSubcategories.${subcategory}`)

			return {
				label,
				value: subcategory as PbSubcategory,
				attrs: { disabled: isDisabled },
			}
		}) ?? []

	return customItemOptions.sort((a, b) => a.label.localeCompare(b.label))
})

/** Submit the form; returning the custom item */
function onSubmit() {
	// Parse the price and make negative if discount
	const price =
		manualAddItem.value.subcategory === PbSubcategory.Discount
			? -Math.abs(Number(manualAddItem.value.price))
			: Number(manualAddItem.value.price)

	const item: FormProps = {
		...manualAddItem.value,
		price,
	}

	if (!item.category) {
		return false
	}

	emit('submit', item as FormProps & Pick<CustomCartItem, 'category'>)
	close()
}

function close() {
	emit('close')
}

function onCategoryInput() {
	formkitReset('subcategory-dropdown')
}

const isGuaranteedItem = computed(() => {
	if (!manualAddItem.value.category) {
		return false
	}

	return [
		PbProductType.Disposition,
		PbProductType.Package,
		PbProductType.Service,
		PbProductType.Transportation,
		PbProductType.Merchandise,
	].includes(manualAddItem.value.category as PbProductType)
})

// Clear the form when the modal is opened
watch(
	() => props.open,
	(isOpen) => {
		if (isOpen) {
			manualAddItem.value = { ...initialValue }
		}
	}
)
</script>

<style scoped>
.list-move,
.list-enter-active,
.list-leave-active {
	transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
	opacity: 0;
	transform: translateY(30px);
	height: 0px;
}

.list-leave-active {
	transform: translateY(-30px) !important;
	opacity: 0;
	transition: all 0.3s ease;
	height: 0px !important;
}
</style>
